/**
 * asconShipment.repository — 경원(아스콘) 수주·계근 조회.
 *
 * 원본: OKIS(Firebird) → 중계 PC 수집기(tools/collector, 15초 간격) → Firestore 미러(읽기 전용, 2026-10-22 rules 배포)
 *   tenants/gyeongwon/units/ascon/ascon_orders   수주 R_SUJU  (문서 = AsconOrder 모양)
 *   tenants/gyeongwon/units/ascon/ascon_tickets  계근 SELL    (문서 = AsconTicket 모양)
 *   tenants/gyeongwon/units/ascon/sync_status/okis  수집기 생존 신호(1분마다, 바뀐 게 있으면 그때마다)
 * 화면은 onSnapshot 으로 구독한다 — 수집기가 쓰는 순간 화면에 나온다(폴링 없음).
 * 쓰기 함수는 만들지 않는다(절대 규칙 1 — 원본은 OKIS, rules 도 사람 쓰기를 막음).
 *
 * 가상 자료(asconShipment.mock.ts)는 시연용으로만: 주소에 `?ascon=mock` 을 붙였을 때.
 */
import { collection, doc, getDocs, onSnapshot, query, where, type DocumentData } from "firebase/firestore";
import { db } from "./firebase";
import { kstToday } from "@/domain/rqc";
import { tenantColPath } from "@/domain/tenantPath";
import { asconMonthBuckets, type AsconMonthBucket, type AsconOrder, type AsconTicket } from "@/domain/asconShipment";
import { mockAsconDay } from "./asconShipment.mock";

export type AsconSource = "mock" | "okis";

export interface AsconDayData {
  orders: AsconOrder[];
  tickets: AsconTicket[];
  source: AsconSource;
  /** 수집기 마지막 회차 시각(생존 신호). 가상 자료면 null */
  syncedAt: Date | null;
  /** 수집기 마지막 회차가 OKIS 읽기에 성공했는지. 모르면 null */
  collectorOk: boolean | null;
}

/** 시연용 가상 자료 — 페이지를 열 때의 주소로 정한다(?ascon=mock) */
const MOCK = typeof window !== "undefined" && new URLSearchParams(window.location.search).get("ascon") === "mock";

const col = (c: "ascon_orders" | "ascon_tickets" | "sync_status") => tenantColPath("gyeongwon", c, "ascon");

const str = (v: unknown): string => (typeof v === "string" ? v : v == null ? "" : String(v));
const num = (v: unknown): number => (typeof v === "number" && Number.isFinite(v) ? v : Number(v) || 0);
const numOrNull = (v: unknown): number | null => (v == null || v === "" ? null : Number.isFinite(Number(v)) ? Number(v) : null);

/** 미러 문서 → 수주. 칸 이름은 수집기(tools/collector/lib/mirror.mjs toOrderDoc)와 같다 */
function toOrder(id: string, d: DocumentData): AsconOrder {
  return {
    id,
    date: str(d.date),
    seq: str(d.seq),
    custCode: str(d.custCode),
    siteCode: str(d.siteCode),
    company: str(d.company),
    site: str(d.site),
    sizeCode: str(d.sizeCode),
    kind: str(d.kind),
    maxAgg: str(d.maxAgg),
    orderT: num(d.orderT),
    shippedT: num(d.shippedT),
    remainT: num(d.remainT),
    state: str(d.state),
    firstTime: str(d.firstTime),
    distKm: numOrNull(d.distKm),
    memo: str(d.memo),
    // 위치(OKIS GROUND) — 수집기가 보내기 시작하면 채워진다(rules 허용 칸 추가 필요)
    ...(d.location ? { location: str(d.location) } : {}),
  };
}

function toTicket(id: string, d: DocumentData): AsconTicket {
  return {
    id,
    date: str(d.date),
    time: str(d.time),
    orderSeq: str(d.orderSeq),
    no: num(d.no),
    carNo: str(d.carNo),
    grossKg: num(d.grossKg),
    tareKg: num(d.tareKg),
    netKg: num(d.netKg),
    tempC: numOrNull(d.tempC),
    operator: str(d.operator),
  };
}

const bySeq = (a: AsconOrder, b: AsconOrder) => a.seq.localeCompare(b.seq);

export interface AsconYearData {
  year: number;
  orders: AsconOrder[];
  tickets: AsconTicket[];
  source: AsconSource;
}

/** 지난 해 자료는 다시 읽지 않는다(밤 전체 맞추기로 고쳐질 수는 있어 새로고침하면 다시 읽음). 올해는 매번 읽는다 */
const yearCache = new Map<number, Promise<AsconYearData>>();

function mockYear(year: number): AsconYearData {
  const today = kstToday();
  const orders: AsconOrder[] = [];
  const tickets: AsconTicket[] = [];
  const end = Math.min(Date.parse(`${year}-12-31T00:00:00Z`), Date.parse(`${today}T00:00:00Z`) + 9 * 86400000);
  for (let t = Date.parse(`${year}-01-01T00:00:00Z`); t <= end; t += 86400000) {
    const d = mockAsconDay(new Date(t).toISOString().slice(0, 10), today);
    orders.push(...d.orders);
    tickets.push(...d.tickets);
  }
  return { year, orders, tickets, source: "mock" };
}

async function readYear(year: number): Promise<AsconYearData> {
  const range = (c: "ascon_orders" | "ascon_tickets") =>
    getDocs(query(collection(db, col(c)), where("date", ">=", `${year}-01-01`), where("date", "<=", `${year}-12-31`)));
  const [os, ts] = await Promise.all([range("ascon_orders"), range("ascon_tickets")]);
  return {
    year,
    orders: os.docs.map((x) => toOrder(x.id, x.data())).sort((a, b) => a.date.localeCompare(b.date) || bySeq(a, b)),
    tickets: ts.docs.map((x) => toTicket(x.id, x.data())).sort((a, b) => a.date.localeCompare(b.date) || byNo(a, b)),
    source: "okis",
  };
}
const byNo = (a: AsconTicket, b: AsconTicket) => a.orderSeq.localeCompare(b.orderSeq) || a.no - b.no;

export const asconShipmentRepo = {
  isMock: MOCK,

  /**
   * 그날 수주·계근 구독. 수주·계근 두 묶음이 모두 한 번 이상 도착한 뒤부터 onData 를 부른다.
   * @returns 구독 해제 함수
   */
  watchDay(date: string, onData: (d: AsconDayData) => void, onError: (e: Error) => void): () => void {
    if (MOCK) {
      let alive = true;
      queueMicrotask(() => {
        if (!alive) return;
        const { orders, tickets } = mockAsconDay(date, kstToday());
        onData({ orders, tickets, source: "mock", syncedAt: null, collectorOk: null });
      });
      return () => { alive = false; };
    }
    let orders: AsconOrder[] | null = null;
    let tickets: AsconTicket[] | null = null;
    let syncedAt: Date | null = null;
    let collectorOk: boolean | null = null;
    const emit = () => {
      if (orders && tickets) onData({ orders, tickets, source: "okis", syncedAt, collectorOk });
    };
    const fail = (e: Error) => onError(e);
    const unsubs = [
      onSnapshot(query(collection(db, col("ascon_orders")), where("date", "==", date)), (s) => {
        orders = s.docs.map((x) => toOrder(x.id, x.data())).sort(bySeq);
        emit();
      }, fail),
      onSnapshot(query(collection(db, col("ascon_tickets")), where("date", "==", date)), (s) => {
        tickets = s.docs.map((x) => toTicket(x.id, x.data())).sort(byNo);
        emit();
      }, fail),
      onSnapshot(doc(db, col("sync_status"), "okis"), (s) => {
        const d = s.data();
        syncedAt = d?.updatedAt?.toDate?.() ?? null;
        collectorOk = typeof d?.ok === "boolean" ? d.ok : null;
        emit();
      }, fail),
    ];
    return () => unsubs.forEach((u) => u());
  },

  /**
   * 그 해 수주·계근 전부(출하실적 화면). 한 해 수주 ~150건·계근 ~1,300건 수준(2019-03 ~, 10-22 미러)이라 한 번에 읽는다.
   * 지난 해는 페이지를 연 동안 캐시, 올해는 부를 때마다 읽는다.
   */
  fetchYear(year: number): Promise<AsconYearData> {
    if (MOCK) return Promise.resolve(mockYear(year));
    const cached = yearCache.get(year);
    if (cached) return cached;
    const p = readYear(year);
    if (year < Number(kstToday().slice(0, 4))) {
      yearCache.set(year, p);
      p.catch(() => yearCache.delete(year));
    }
    return p;
  },

  /**
   * 기간 수주(날짜 포함 범위) — 내 업무 홈 다가오는 일정·일정 달력(2026-10-22). 계근은 읽지 않는다.
   * 한 달 수주 ~20건 수준이라 한 번 읽기(구독 없음).
   */
  async fetchOrders(from: string, to: string): Promise<{ orders: AsconOrder[]; source: AsconSource }> {
    if (MOCK) {
      const today = kstToday();
      const orders: AsconOrder[] = [];
      for (let t = Date.parse(`${from}T00:00:00Z`); t <= Date.parse(`${to}T00:00:00Z`); t += 86400000) {
        orders.push(...mockAsconDay(new Date(t).toISOString().slice(0, 10), today).orders);
      }
      return { orders, source: "mock" };
    }
    const s = await getDocs(
      query(collection(db, col("ascon_orders")), where("date", ">=", from), where("date", "<=", to)),
    );
    return {
      orders: s.docs.map((x) => toOrder(x.id, x.data())).sort((a, b) => a.date.localeCompare(b.date) || bySeq(a, b)),
      source: "okis",
    };
  },

  /**
   * 그 해 월별 출하(톤) — 그 해 수주의 출하누계를 월별로 더한다(수주 ~200건/년 수준이라 한 번에 읽음).
   * 홈 위젯 여러 개(당월 KPI·월별 차트·표)가 같은 해를 부르므로 같은 해는 1분 동안 한 번만 읽는다(지난 해는 페이지를 연 동안).
   */
  fetchMonthly(year: number): Promise<{ buckets: AsconMonthBucket[]; source: AsconSource }> {
    const hit = monthCache.get(year);
    const past = year < Number(kstToday().slice(0, 4));
    if (hit && (past || Date.now() - hit.at < MONTH_TTL_MS)) return hit.p;
    const p = readMonthly(year);
    monthCache.set(year, { at: Date.now(), p });
    p.catch(() => monthCache.delete(year));
    return p;
  },
};

const MONTH_TTL_MS = 60_000;
const monthCache = new Map<number, { at: number; p: Promise<{ buckets: AsconMonthBucket[]; source: AsconSource }> }>();

async function readMonthly(year: number): Promise<{ buckets: AsconMonthBucket[]; source: AsconSource }> {
  if (MOCK) {
    const today = kstToday();
    const orders: AsconOrder[] = [];
    const end = Math.min(Date.parse(`${year}-12-31T00:00:00Z`), Date.parse(`${today}T00:00:00Z`) + 9 * 86400000);
    for (let t = Date.parse(`${year}-01-01T00:00:00Z`); t <= end; t += 86400000) {
      orders.push(...mockAsconDay(new Date(t).toISOString().slice(0, 10), today).orders);
    }
    return { buckets: asconMonthBuckets(year, orders), source: "mock" };
  }
  const s = await getDocs(
    query(collection(db, col("ascon_orders")), where("date", ">=", `${year}-01-01`), where("date", "<=", `${year}-12-31`)),
  );
  return { buckets: asconMonthBuckets(year, s.docs.map((x) => toOrder(x.id, x.data()))), source: "okis" };
}
